import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, User, Wallet, Network, Eye } from 'lucide-react';
import { transactionsService } from '../../services/transactionsServices';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MEDIA_URL } from '../../global_constants';

const TransactionDetails = ({ onStatusUpdate }) => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    if (transactionId) {
      fetchTransactionDetails();
    }
  }, [transactionId]);

  const fetchTransactionDetails = async () => {
    try {
      setLoading(true);
      
      const response = await transactionsService.getTransactionDetails(transactionId);
      
      if (response.status === 200) {
        setTransaction(response?.data?.data);
      } else {
        toast.error(response?.message || 'Failed to fetch transaction details');
      }
    } catch (err) {
      toast.error('Error fetching transaction details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      setUpdating(true);

      const statusData = {
        status: newStatus
      }
      
      const response = await transactionsService.transactionStatusUpdate(transactionId, statusData);
      
      if (response.status === 200) {
        setTransaction(prev => ({
          ...prev,
          status: newStatus,
          updated_at: new Date().toISOString()
        }));
        
        if (onStatusUpdate) {
          onStatusUpdate(response?.data?.data);
        }
        
        toast.success(`Transaction ${newStatus} successfully!`);
      } else {
        toast.error(response.message || `Failed to ${newStatus} transaction`);
      }
    } catch (err) {
      toast.error('Error updating transaction status');
    } finally {
      setUpdating(false);
    }
  };

  const getBadgeColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning" role="alert">
          <XCircle className="me-2" size={20} />
          Transaction not found.
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <h2>Transaction #{transaction.transaction_id}</h2>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      <div className="card">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Transaction Details</h4>
          <small className="text-white-50">Topup Wallet Transaction</small>
        </div>
        
        <div className="card-body">
          {/* Status Section */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <strong>Status:</strong>{' '}
                <span className={`badge bg-${getBadgeColor(transaction.status)} ms-2`}>
                  {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                </span>
              </div>
              <div className="text-muted">
                <small>Last updated: {formatDate(transaction.updated_at)}</small>
              </div>
            </div>

            {transaction.status === 'pending' && (
              <div className="d-flex gap-2 mb-3">
                <button
                  onClick={() => handleStatusUpdate('approved')}
                  disabled={updating}
                  className="btn btn-success d-flex align-items-center"
                >
                  {updating ? (
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <CheckCircle className="me-2" size={16} />
                  )}
                  Approve
                </button>
                <button
                  onClick={() => handleStatusUpdate('cancelled')}
                  disabled={updating}
                  className="btn btn-danger d-flex align-items-center"
                >
                  {updating ? (
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <XCircle className="me-2" size={16} />
                  )}
                  Cancel
                </button>
              </div>
            )}

            {transaction.status !== 'pending' && (
              <div className="alert alert-info" role="alert">
                <small>
                  This transaction has been {transaction.status} and cannot be modified.
                </small>
              </div>
            )}
          </div>

          {/* Transaction Details */}
          <div className="row">
            <div className="col-md-6">
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">
                    <User className="me-2" size={20} />
                    User Information
                  </h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <strong>Full Name:</strong> {transaction.user.fullName || 'N/A'}
                  </div>
                  <div className="mb-3">
                    <strong>Email:</strong> {transaction.user.email}
                  </div>
                  <div className="mb-3">
                    <strong>Phone:</strong> {transaction.user.phoneNumber || 'N/A'}
                  </div>
                  <div className="mb-3">
                    <strong>User ID:</strong>{' '}
                    <span className="text-muted font-monospace">{transaction.user.id}</span>
                  </div>
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">
                    <Wallet className="me-2" size={20} />
                    Amount Details
                  </h5>
                </div>
                <div className="card-body text-center">
                  <div className="display-6 text-primary fw-bold mb-2">
                    {formatAmount(transaction.amount)}
                  </div>
                  <div className="text-muted">Topup Amount</div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">
                    <Network className="me-2" size={20} />
                    Transaction Information
                  </h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <strong>Transaction ID:</strong>{' '}
                    <span className="text-muted font-monospace">{transaction.transaction_id}</span>
                  </div>
                  <div className="mb-3">
                    <strong>Network:</strong> {transaction.network}
                  </div>
                  <div className="mb-3">
                    <strong>Wallet Address:</strong>
                    <div className="text-muted font-monospace small text-break">
                      {transaction.deposit_wallet_address}
                    </div>
                  </div>
                  {transaction.transaction_hash && (
                    <div className="mb-3">
                      <strong>Transaction Hash:</strong>
                      <div className="text-muted font-monospace small text-break">
                        {transaction.transaction_hash}
                      </div>
                    </div>
                  )}
                  <div className="mb-3">
                    <strong>Created:</strong> {formatDate(transaction.created_at)}
                  </div>
                </div>
              </div>

              {transaction.transaction_image && (
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="mb-0">Transaction Proof</h5>
                  </div>
                  <div className="card-body">
                    <div className="position-relative">
                      <img
                        src={`${MEDIA_URL}/uploads/${transaction.transaction_image}`}
                        alt="Transaction proof"
                        className="img-fluid rounded cursor-pointer"
                        style={{ height: '200px', objectFit: 'cover', width: '100%' }}
                        onClick={() => setShowImageModal(true)}
                      />
                      <button
                        onClick={() => setShowImageModal(true)}
                        className="btn btn-light position-absolute top-0 end-0 m-2 rounded-circle p-2"
                        style={{ width: '40px', height: '40px' }}
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && transaction.transaction_image && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Transaction Proof</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImageModal(false)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <img
                  src={`${MEDIA_URL}/uploads/${transaction.transaction_image}`}
                  alt="Transaction proof"
                  className="img-fluid rounded"
                  style={{ maxHeight: '70vh' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionDetails;